import {
  Badge,
  Box,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { featureCollection } from '@turf/turf';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef } from 'react-map-gl';

import { ParcellesFieldPopupData } from '../../components/parcelles-field-popup/parcelles-field-popup';
import { useContractsQueries } from '../../lib/queries/contracts';

import { ContractParcellesMap } from './contract-parcelles-map';
import {
  buildFieldFeatureIndex,
  buildFilteredLayers,
  buildStackFieldFromRow,
  buildTableRows,
  filterTableRows,
  ParcellesTableRow,
} from './contract-parcelles-utils';

import { zoomOnFeature } from '@map';
import { PfTable } from '@ui';
import { MRT_ColumnDef, MRT_ColumnFiltersState } from 'mantine-react-table';

export function ContractParcellesPage() {
  const { t } = useTranslation();
  const { contractId } = useParams({
    from: '/_app/os/contracts_/$contractId/parcelles',
  });
  const mapRef = useRef<MapRef>(null);
  const mapInitializedRef = useRef(false);
  const filtersInitializedRef = useRef(false);
  const [visibleCampaignIds, setVisibleCampaignIds] = useState<number[]>([]);
  const [focusedCampaignId, setFocusedCampaignId] = useState<number | null>(null);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const deferredColumnFilters = useDeferredValue(columnFilters);
  const [popupData, setPopupData] = useState<ParcellesFieldPopupData | null>(
    null,
  );

  const contractsQueries = useContractsQueries();
  const { data, isLoading } = useQuery({
    ...contractsQueries.ilotFieldsDetail(Number(contractId)),
  });

  useEffect(() => {
    if (!data || filtersInitializedRef.current) {
      return;
    }
    setVisibleCampaignIds(data.filters.campaigns.map((campaign) => campaign.id));
    filtersInitializedRef.current = true;
  }, [data]);

  const visibleLayers = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.layers.filter((layer) =>
      visibleCampaignIds.includes(layer.campaign_id),
    );
  }, [data, visibleCampaignIds]);

  const tableRows = useMemo(() => buildTableRows(visibleLayers), [visibleLayers]);

  const filteredTableRows = useMemo(
    () => filterTableRows(tableRows, columnFilters),
    [tableRows, columnFilters],
  );

  const mapFilteredRows = useMemo(
    () => filterTableRows(tableRows, deferredColumnFilters),
    [tableRows, deferredColumnFilters],
  );

  const filteredLayers = useMemo(() => {
    const allowedFieldIds = new Set(mapFilteredRows.map((row) => row.id));
    return buildFilteredLayers(visibleLayers, allowedFieldIds);
  }, [visibleLayers, mapFilteredRows]);

  const featureByFieldId = useMemo(
    () => buildFieldFeatureIndex(filteredLayers),
    [filteredLayers],
  );

  const fieldRowById = useMemo(() => {
    const index = new Map<number, ParcellesTableRow>();
    for (const row of tableRows) {
      index.set(row.id, row);
    }
    return index;
  }, [tableRows]);

  const visibleSummary = useMemo(
    () =>
      tableRows.reduce(
        (acc, row) => ({
          count: acc.count + 1,
          total_area: acc.total_area + (row.area ?? 0),
        }),
        { count: 0, total_area: 0 },
      ),
    [tableRows],
  );

  const filteredSummary = useMemo(
    () =>
      filteredTableRows.reduce(
        (acc, row) => ({
          count: acc.count + 1,
          total_area: acc.total_area + (row.area ?? 0),
        }),
        { count: 0, total_area: 0 },
      ),
    [filteredTableRows],
  );

  const columns = useMemo<MRT_ColumnDef<ParcellesTableRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        minSize: 72,
        size: 88,
        grow: 1,
      },
      {
        accessorKey: 'telepac_code',
        header: 'Telepac',
        minSize: 88,
        size: 104,
        grow: 3,
        Cell: ({ cell }) => (
          <Text fw={500} size="sm">
            {cell.getValue<string | null>() ?? '-'}
          </Text>
        ),
      },
      {
        accessorKey: 'campaign_year',
        header: t('common.campaign'),
        minSize: 84,
        size: 96,
        grow: 1,
      },
      {
        accessorKey: 'identification_label',
        header: t('contracts.identification-source'),
        minSize: 112,
        size: 124,
        maxSize: 148,
        grow: 0,
        Cell: ({ row }) => (
          <Group gap={6} wrap="wrap">
            <Badge
              color={
                row.original.identification_source === 'TELEPAC'
                  ? 'blue'
                  : 'grape'
              }
              size="sm"
              variant="light"
            >
              {row.original.identification_label}
            </Badge>
            {row.original.reference_campaign_year != null ? (
              <Text c="dimmed" size="xs">
                {t('contracts.reference-campaign', {
                  year: row.original.reference_campaign_year,
                })}
              </Text>
            ) : null}
          </Group>
        ),
      },
    ],
    [t],
  );

  const initialMapBounds = useMemo(() => {
    if (!visibleLayers.length) {
      return null;
    }
    return featureCollection(
      visibleLayers.flatMap((layer) => layer.fields.features),
    );
  }, [visibleLayers]);

  const handleZoomOnField = useCallback(
    (fieldId: number) => {
      const feature = featureByFieldId.get(fieldId);
      if (feature && mapRef.current) {
        zoomOnFeature({
          map: mapRef.current.getMap(),
          geojson: feature,
        });
      }
    },
    [featureByFieldId],
  );

  const handleLoad = useCallback(
    (map: MapEvent) => {
      if (initialMapBounds?.features.length && !mapInitializedRef.current) {
        zoomOnFeature({
          map: map.target,
          geojson: initialMapBounds,
        });
        mapInitializedRef.current = true;
      }
    },
    [initialMapBounds],
  );

  useEffect(() => {
    mapInitializedRef.current = false;
  }, [visibleCampaignIds, contractId]);

  const handleBadgeClick = useCallback(
    (campaignId: number) => {
      setVisibleCampaignIds((previous) => {
        if (previous.includes(campaignId)) {
          if (previous.length === 1) {
            return previous;
          }
          const next = previous.filter((id) => id !== campaignId);
          if (focusedCampaignId === campaignId) {
            setFocusedCampaignId(null);
          }
          return next;
        }
        return [...previous, campaignId].sort(
          (a, b) =>
            (data?.filters.campaigns.find((c) => c.id === a)?.year ?? 0) -
            (data?.filters.campaigns.find((c) => c.id === b)?.year ?? 0),
        );
      });
    },
    [data?.filters.campaigns, focusedCampaignId],
  );

  const handleBadgeDoubleClick = useCallback((campaignId: number) => {
    setVisibleCampaignIds((previous) =>
      previous.includes(campaignId) ? previous : [...previous, campaignId],
    );
    setFocusedCampaignId((previous) =>
      previous === campaignId ? null : campaignId,
    );
  }, []);

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      const map = mapRef.current?.getMap();
      if (!map) {
        return;
      }

      const features = map.queryRenderedFeatures(event.point, {
        layers: filteredLayers.flatMap((layer) => [
          `parcelles-${layer.campaign_id}-fill`,
          `parcelles-${layer.campaign_id}-outline`,
        ]),
      });

      if (!features.length) {
        setPopupData(null);
        return;
      }

      const stackFields = [];
      const seen = new Set<number>();

      for (const feature of features) {
        const fieldId = (feature.id ?? feature.properties?.id) as
          | number
          | undefined;
        if (fieldId == null || seen.has(fieldId)) {
          continue;
        }
        seen.add(fieldId);

        const row = fieldRowById.get(fieldId);
        if (!row) {
          continue;
        }

        stackFields.push(buildStackFieldFromRow(row));
      }

      stackFields.sort((a, b) => b.campaignYear - a.campaignYear);

      setPopupData({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        fields: stackFields,
      });
    },
    [fieldRowById, filteredLayers],
  );

  if (isLoading && !data) {
    return <Skeleton h="100%" width="100%" />;
  }

  if (!data) {
    return null;
  }

  return (
    <Stack flex={1} gap="md" h="100%" style={{ minHeight: 0, overflow: 'hidden' }}>
      <Paper p="md" style={{ flexShrink: 0 }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={3}>{t('contracts.parcelles-title')}</Title>
            <Group gap={4}>
              <Text>
                {filteredSummary.count} {t('common.parcelles-engagees')} ·{' '}
                {filteredSummary.total_area.toFixed(2)} ha
              </Text>
              {columnFilters.length > 0 ? (
                <Text c="dimmed" size="sm">
                  / {visibleSummary.count}
                </Text>
              ) : null}
            </Group>
          </Group>

          <Text c="dimmed" size="xs">
            {t('contracts.parcelles-badge-hint')}
          </Text>

          <Group gap="sm">
            {data.filters.campaigns.map((campaign) => {
              const layer = data.layers.find(
                (item) => item.campaign_id === campaign.id,
              );
              const isVisible = visibleCampaignIds.includes(campaign.id);
              const isFocused = focusedCampaignId === campaign.id;
              const color = layer?.color ?? '#888';

              return (
                <UnstyledButton
                  key={campaign.id}
                  onClick={() => handleBadgeClick(campaign.id)}
                  onDoubleClick={() => handleBadgeDoubleClick(campaign.id)}
                >
                  <Badge
                    leftSection={
                      <Box
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: color,
                        }}
                      />
                    }
                    opacity={isVisible ? 1 : 0.45}
                    style={{
                      borderColor: color,
                      cursor: 'pointer',
                      ...(isFocused
                        ? {
                            backgroundColor: `${color}22`,
                            boxShadow: `0 0 0 2px ${color}`,
                          }
                        : {}),
                    }}
                    variant={isVisible ? 'outline' : 'light'}
                  >
                    {campaign.year} ({layer?.stats.count ?? 0})
                  </Badge>
                </UnstyledButton>
              );
            })}
          </Group>
        </Stack>
      </Paper>

      <Group
        align="stretch"
        flex={1}
        gap="md"
        wrap="nowrap"
        style={{ minHeight: 0, overflow: 'hidden' }}
      >
        <Box
          style={{
            flex: '1 1 46%',
            maxWidth: '52%',
            minHeight: 0,
            minWidth: 380,
            overflow: 'hidden',
          }}
        >
          <PfTable<ParcellesTableRow>
            enableShowHideColumns={false}
            options={{
              data: tableRows,
              columns,
              enablePagination: false,
              enableRowVirtualization: true,
              enableFullScreenToggle: false,
              enableRowActions: false,
              enableColumnDragging: false,
              enableColumnResizing: false,
              layoutMode: 'grid',
              getRowId: (row) => `${row.id}-${row.campaign_year}`,
              initialState: {
                sorting: [{ id: 'campaign_year', desc: true }],
              },
              state: {
                columnFilters,
              },
              onColumnFiltersChange: setColumnFilters,
              mantinePaperProps: {
                h: '100%',
                miw: 0,
                w: '100%',
                style: { display: 'flex', flexDirection: 'column' },
              },
              mantineTableContainerProps: {
                style: { overflowX: 'auto' },
              },
              mantineTableProps: {
                layout: 'fixed',
                style: { tableLayout: 'fixed', width: '100%' },
                verticalSpacing: 'sm',
              },
              mantineTableBodyCellProps: {
                style: { paddingInline: 10, verticalAlign: 'middle' },
              },
              mantineTableHeadCellProps: {
                style: {
                  fontSize: '12px',
                  lineHeight: 1.25,
                  paddingInline: 10,
                  verticalAlign: 'bottom',
                  whiteSpace: 'nowrap',
                },
              },
              mantineTableBodyRowProps: ({ row }) => ({
                onClick: () => handleZoomOnField(row.original.id),
                style: { cursor: 'pointer' },
              }),
            }}
          />
        </Box>
        <Paper flex={1} h="100%" p={0} style={{ minHeight: 0, overflow: 'hidden' }}>
          <ContractParcellesMap
            filteredLayers={filteredLayers}
            focusedCampaignId={focusedCampaignId}
            mapRef={mapRef}
            onClosePopup={() => setPopupData(null)}
            onLoad={handleLoad}
            onMapClick={handleMapClick}
            popupData={popupData}
          />
        </Paper>
      </Group>
    </Stack>
  );
}
